import { FocusMonitor } from '@angular/cdk/a11y';
import { Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Catch } from '../shared/catch.model';
import { CommonFunctionService } from '../shared/common-function-service.service';
import { LocalStorageService } from '../shared/local-storage.service';
import { LookupDataService } from '../shared/lookup-data.service';
import { LookupItem } from '../shared/lookup-item.model';
import { speciesValidator } from '../shared/trip-validator.model';


/**
 * Dialog to select catch species, either typed from an autocomplete, selected from a previous species, or selected from favorite species.
 * Two screens here, first to select the species, second to enter its weights.
 */
@Component({
    selector: 'app-add-catch-dialog',
    templateUrl: './add-catch-dialog.component.html',
    styleUrls: ['./add-catch-dialog.component.css'],
    standalone: false
})
export class AddCatchDialogComponent implements OnInit, OnDestroy {

  @ViewChild('speciesInput') speciesInput!: ElementRef;

  catchFormGroup!: FormGroup;

  speciesOptions: LookupItem[] = [];
  filteredSpecies!: Observable<LookupItem[]>;
  speciesSubscription!: Subscription;

  speciesName: string | null = '';
  weightsSection: boolean = false;

  // species lists
  favoriteSpecies: LookupItem[] = [];
  previousSpecies: LookupItem[] = [];
  prioritySpeciesSelected: boolean = false;

  constructor(
    private common: CommonFunctionService,
    public router: Router,
    public dialogRef: MatDialogRef<AddCatchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {catch: Catch, fishery: string, EMTrip: boolean, prioritySpecies: LookupItem[]},
    private lookup: LookupDataService,
    public focusMonitor: FocusMonitor,
    public ngZone: NgZone,
    private toastr: ToastrService,

  ) {}

  /**
   * Initialization -- Set the catch form and class variables. Go to the weight section if an existing species is being edited.
   */
  ngOnInit(): void {

    this.catchFormGroup = new FormGroup({
      species: new FormControl('', [Validators.required, speciesValidator()]),
      retainedCount: new FormControl(0, Validators.min(0)),
      retainedWeight: new FormControl(0, Validators.min(0)),
      releasedCount: new FormControl(0, Validators.min(0)),
      releasedWeight: new FormControl(0, Validators.min(0))
    }, { validators: this.countAndWeightValidator });

    this.catchFormGroup.patchValue({
      species: this.data.catch.species,
      retainedCount: this.data.catch.retainedCount,
      retainedWeight: this.data.catch.retainedWeight,
      releasedCount: this.data.catch.releasedCount,
      releasedWeight: this.data.catch.releasedWeight
    });

    

    this.speciesName = this.data.catch.species.name;
    this.prioritySpeciesSelected = (this.data.prioritySpecies ?? []).some(sp => sp.name == this.speciesName);

    // species already selected
    this.weightsSection = this.speciesName != '';

    if (this.data.fishery == 'Trawl') this.setTrawlValidation();

    // species not selected yet
    if (!this.weightsSection) {
      this.catchFormGroup.get('species')!.valueChanges.subscribe(val => {
        this.speciesName = val!.name;
      });

      this.speciesSubscription = this.lookup.getSpeciesObservable(this.data.fishery).subscribe((value) => {
        this.speciesOptions = value;
      });

      this.filteredSpecies = this.catchFormGroup.get('species')!.valueChanges.pipe(
        startWith(''),
        map((value: any) => this.speciesOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );

      // get favorite species list
      this.favoriteSpecies = LocalStorageService.current_user.favoriteSpecies.filter(sp => sp.lookupType == 'SPECIES');

      // get species from previous sets
      LocalStorageService.user_trip.sets.filter(s => s.fishery == this.data.fishery).forEach(set => {
        set.catchItems.forEach(ci => {
          if (ci.species.name != '' && !this.previousSpecies.find(x => x.name == ci.species.name) && !this.favoriteSpecies.find(x => x.name == ci.species.name)) {
            this.previousSpecies.push(ci.species);
          }
        });
      });

    }

  }


  setTrawlValidation() {
    const setValidators = (controlName: string) => {
      this.catchFormGroup.get(controlName)?.setValidators([Validators.required, Validators.min(0)]);
    };

    if (this.weightsSection && this.prioritySpeciesSelected) setValidators('retainedCount');    
    setValidators('retainedWeight');

    if (this.data.EMTrip) {
      if (this.weightsSection && this.prioritySpeciesSelected) setValidators('releasedCount');
      setValidators('releasedWeight');
    }

    if (this.prioritySpeciesSelected) this.catchFormGroup.setValidators(this.countAndWeightValidator);
    else this.catchFormGroup.removeValidators(this.countAndWeightValidator);

    this.catchFormGroup.updateValueAndValidity();   
  }


  /**
   * Adding a new favorite species done as in the Settings component
   */
  addSpecies(): void {
    this.common.addSpecies(-1, this.data.fishery as 'Trawl' | 'Combined' | 'HMS' | 'Groundfish').finally(() => this.favoriteSpecies = LocalStorageService.current_user.favoriteSpecies.filter(sp => sp.lookupType == 'SPECIES'));
  }

  /**
   * Quick selection from either favorite or previous species list
   * @param item 
   */
  quickSelect(item: LookupItem) {
    this.catchFormGroup.get('species')?.setValue(item);
    this.confirmSpecies();
  }


  /**
   * After confirming a valid species in the catch form
   */
  confirmSpecies() {
    const speciesControl = this.catchFormGroup.get('species');
    speciesControl?.markAsTouched();

    if (speciesControl?.valid) {
      this.prioritySpeciesSelected = (this.data.prioritySpecies ?? []).some(sp => sp.name == speciesControl?.value.name);  
      this.weightsSection = true;
      if (this.data.fishery == 'Trawl') this.setTrawlValidation();
    }
  }


  /**
   * Check for valid catch form and weights, then close the dialog with result
   * @returns 
   */
  confirmCatch() {
    if (this.catchFormGroup.invalid || !this.weightsSection) {
      this.toastr.error("Pieces and weight must both be filled", "Invalid form");
      return;
    } 
    this.dialogRef.close(this.catchFormGroup.value);
  }

  /**
   * If enter key pressed
   * @param key Key pressed
   */
  handleKeyUp(key: any) {
    if (key.keyCode == 13) this.confirmCatch();
  }


  onNoClick(): void {
    this.dialogRef.close();
  }

  public removeFocus(field: MatFormField, input: HTMLInputElement) {
    setTimeout(() => {
      input.blur();
    }, 50);
 }


  ngOnDestroy(): void {
    if (this.speciesSubscription != null) this.speciesSubscription.unsubscribe();
  }


  _forbidNonNumeric(control: AbstractControl) {
      control.setValue(control.value.replace(/[^0-9]/g, ''), {onlySelf: true, emitEvent: false});   
  }

 
  /**
  * Custom validator function
  */
  countAndWeightValidator: ValidatorFn = (control: AbstractControl) => {
    const retainedCount = +control.get('retainedCount')?.value;
    const retainedWeight = +control.get('retainedWeight')?.value;
    const releasedCount = +control.get('releasedCount')?.value;
    const releasedWeight = +control.get('releasedWeight')?.value;

    // Check if either count or weight is non-zero for both retained and released groups
    const retainedInvalid = (retainedCount > 0 && !retainedWeight) || (!retainedCount && retainedWeight > 0);
    const releasedInvalid = (releasedCount > 0 && !releasedWeight) || (!releasedCount && releasedWeight > 0);

    return retainedInvalid || releasedInvalid ? { countAndWeightInvalid: true } : null;
  }

}
